import { ShareService, TokenProvider, ITelemetryNamespaceLogger } from '@fluidx/office-fluid-types';
import { NamespaceLoggerAdapter, Locale, LocaleDirection } from '@fluidx/utilities';

/**
 * A helper method that returns an implementation of ShareService for containers backed by Sharepoint.
 * @param tokenProvider A token provider that can return Sharepoint access tokens
 * @param logger Optional logger for telemetry
 * @param appName Optional appName to be encoded to the generated links
 * @param bootDocumentInfo ODSP boot document used as share service context when container resolved url is undefined
 */
export function getShareServiceForSharepoint({
  tokenProvider,
  logger,
  appName,
  bootDocumentInfo,
  localization,
  tenantId,
  getShareUXTargetElement
}: {
  bootDocumentInfo: {
    /** The file/item ID of the underlying Sharepoint file */
    fileId: string;
    /** The drive ID of the underlying Sharepoint file */
    driveId: string;
    /** The site URL of the underlying Sharepoint file */
    siteUrl: string;
  };
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };
  tokenProvider: TokenProvider;
  logger?: ITelemetryNamespaceLogger;
  appName?: string;
  /**
   * Tenant id is used to designate AAD tenant where document resides. This can be specified in shape of GUID or
   * domain hostname. If not specified then document is presumed to reside in the tenancy of currently signed in user.
   */
  tenantId?: string;
  /**
   * When rendering share UX to the DOM, the scope instance uses this callback to get an element which
   * it can associate to a window or location on the DOM. to share UX like dialogs and callouts
   * ReactDOM is used for rendering share UX like dialogs and callouts on the returned element,
   * and so the element gets cleared of content
   */
  getShareUXTargetElement?: () => HTMLElement;
}): Promise<ShareService> {
  return import('./SharepointShareService').then((m) => {
    const shareServiceLogger = logger ? new NamespaceLoggerAdapter('ShareService', logger) : undefined;
    return new m.SharepointShareService(
      bootDocumentInfo,
      localization,
      tokenProvider,
      shareServiceLogger,
      appName,
      tenantId,
      getShareUXTargetElement
    );
  });
}
